import { auth } from '../../../../firebase';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getRolePhaseCodes,
  getRolesInfo,
  postRoleArgs,
} from '../../../services/admin/RolesService';
import { useRolesAccessContext } from '../../../context/RolesAccessContext';
import { removeDuplicates } from '../../../../utils/roleUtils';
import { Access } from '../../../Interfaces/admin/Role.interfaces';
import { getUserSettings } from '../../../services/admin/UsersService';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';

type GetRolesProps = {
  children?: React.ReactNode;
};

const GetRoles = ({ children }: GetRolesProps) => {
  const { setRolesAccess, setIsAdmin, setUserType, setRolePhases, setEmailNotificationsEnabled } =
    useRolesAccessContext();
  const [roles, setRoles] = useState<string[]>();
  const [access, setAccess] = useState<Access[]>([]);
  const [accessLoading, setAccessLoading] = useState<boolean>(true);
  const [roleLoading, setRoleLoading] = useState<boolean>(true);
  const toast = useRef<Toast>(null);

  const rolePhases = useQuery({
    queryKey: ['getRolePhases'],
    queryFn: ({ signal }) => {
      return getRolePhaseCodes(signal);
    },
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setRolePhases(data);
      setRoleLoading(false);
    },
    enabled: false,
  });

  const { isLoading } = useQuery({
    queryKey: ['getUserRolesInfo', roles],
    queryFn: ({ signal }) => {
      return getRolesInfo(roles ?? [], signal);
    },
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      let list: Access[] = [];
      let shouldHidePhases = false;
      data.forEach((role: postRoleArgs) => {
        list = [...list, ...role.access];
        shouldHidePhases = shouldHidePhases || !!role.hidePhaseCodes;
      });

      setAccess((access) => {
        if (access.length > 0) {
          return removeDuplicates([...access, ...list]).filter(
            (acc) => !acc.blocked
          );
        } else {
          return removeDuplicates(list).filter((acc) => !acc.blocked);
        }
      });

      if (shouldHidePhases) {
        rolePhases.refetch();
      } else {
        setRoleLoading(false);
      }
    },
    enabled: !!roles,
  });

  useQuery({
    queryKey: ['getUserSettings'],
    queryFn: ({ signal }) => {
      return getUserSettings(signal);
    },
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const userAccess: Access[] = data?.access || [];

      setAccess((access) => {
        const userAccessSet = new Set(userAccess.map((acc) => acc.report));

        if (access.length > 0) {
          const noRepeatedAccess = access.filter(
            (roleAcc) => !userAccessSet.has(roleAcc.report)
          );

          return [
            ...userAccess.filter((acc) => !acc.blocked),
            ...noRepeatedAccess,
          ];
        } else {
          return userAccess.filter((acc) => !acc.blocked);
        }
      });

      setEmailNotificationsEnabled(data?.emailNotificationsEnabled ?? true);
      setAccessLoading(false);
    },
    enabled: !isLoading,
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        auth.currentUser
          ?.getIdTokenResult(true)
          .then((tokenData) => {
            const roles = tokenData?.claims['roles'];
            const isAdmin = tokenData?.claims['isAdmin'];
            const userType = tokenData?.claims['userType'];

            setIsAdmin(isAdmin);
            setUserType(userType);

            if (roles?.length > 0) {
              setRoles(roles);
            }
          })
          .catch((error) => {
            console.log(`User token error: ${error}`);
          });
      }
    });

    return () => unsubscribe();
  }, [setIsAdmin, setUserType]);

  useEffect(() => {
    setRolesAccess(access);
  }, [access, setRolesAccess]);

  useEffect(() => {
    if (
      !(accessLoading || (roles && roleLoading)) &&
      !auth.currentUser?.providerData.some(
        (provider) => provider.providerId === 'microsoft.com'
      )
    ) {
      showToast(
        'info',
        toast,
        'Link Microsoft Account',
        'You can now link your Microsoft account from the user menu',
        6000
      );
    }
  }, [accessLoading, roles, roleLoading, toast]);

  if (accessLoading || (roles && roleLoading)) {
    return (
      <div className="h-screen flex align-items-center">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <React.Fragment>
      {children}
      <ErrorToast toastRef={toast} position="top-left" />
    </React.Fragment>
  );
};

export default GetRoles;
